import { safeSlug } from "@/lib/utils"
import { getContentPrefixOrDefault } from "./blogPrefixMap"
import { isTrimbleDeveloperNewsContentPrefix } from "./trimbleDeveloperNews"

/**
 * Content-type names (lowercase) to the URL path segment they use.
 * Matches the existing CONTENT_TYPE_PATH_PREFIX in the revalidation route
 * and TYPE_SEGMENT_MAP in feedConstants (which uses __typename casing).
 */
const TYPE_TO_SEGMENT: Record<string, string> = {
  article: "article",
  Article: "article",
  caseStudy: "case-study",
  CaseStudy: "case-study",
  customerStory: "customer-story",
  CustomerStory: "customer-story",
  video: "video",
  Video: "video",
  document: "docs",
  Document: "docs",
  externalResource: "external-resource",
  ExternalResource: "external-resource",
}

/**
 * Build a fully-qualified RC content URL with the correct path prefix.
 *   Articles:     `/en-US/blog/trimble/article/my-article-slug`
 *   Other types:  `/en-US/resources/trimble/case-study/my-case-study`
 *
 * @param lang          Locale code, e.g. `en-US`
 * @param type          Content-type name or __typename (e.g. `article`, `Article`, `CaseStudy`)
 * @param slug          Entry slug (will be sanitised via `safeSlug`)
 * @param contentPrefix Optional explicit prefix (e.g. "blog/trimble" or "resources/trimble").
 *                      When omitted the default is derived from the content type.
 * @param entry         Optional entry object used to derive the center slug from tags.
 */
export function buildRcUrl(
  lang: string,
  type: string,
  slug: string,
  contentPrefix?: string,
  entry?: any,
): string {
  const segment = TYPE_TO_SEGMENT[type] || type
  const prefix = contentPrefix ?? getContentPrefixOrDefault(entry, type)

  if (isTrimbleDeveloperNewsContentPrefix(prefix) && segment === "article") {
    return `/${lang}/${prefix}/${safeSlug(slug)}`
  }

  return `/${lang}/${prefix}/${segment}/${safeSlug(slug)}`
}

/**
 * Resolve the URL path segment for a given content type.
 * Useful when callers need just the segment without the full URL.
 */
export function rcTypeSegment(type: string): string {
  return TYPE_TO_SEGMENT[type] || type
}
